/* PetIT — storefront interactions */
(() => {
  const qs = (selector, root = document) => root.querySelector(selector);
  const qsa = (selector, root = document) => [...root.querySelectorAll(selector)];

  const formatMoney = (cents) => {
    const currency = window.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency
      }).format(cents / 100);
    } catch (_) {
      return (cents / 100).toFixed(2);
    }
  };

  const cartDrawer = () => qs('[data-cart-drawer]');
  const setCartOpen = (open) => {
    const drawer = cartDrawer();
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
    document.documentElement.classList.toggle('cart-drawer-open', open);
    if (open) qs('[data-cart-close]', drawer)?.focus();
  };

  const updateFreeShippingProgress = (totalCents) => {
    const progress = qs('[data-free-shipping-progress]');
    if (!progress) return;
    const threshold = parseInt(progress.dataset.thresholdCents || '5000', 10);
    const total = Math.max(0, Number(totalCents) || 0);
    const percent = total >= threshold ? 100 : Math.min(99, Math.floor((total / threshold) * 100));
    const fill = qs('[data-free-shipping-fill]', progress);
    const message = qs('[data-free-shipping-message]', progress);
    const percentEl = qs('[data-free-shipping-percent]', progress);
    if (fill) fill.style.width = `${percent}%`;
    if (percentEl) percentEl.textContent = `${percent}%`;
    if (message) {
      message.textContent = total >= threshold
        ? 'Free shipping unlocked ✦'
        : `Add ${formatMoney(threshold - total)} for free shipping`;
    }
  };

  const updateCartBadges = (count) => {
    qsa('[data-cart-count]').forEach((el) => {
      el.textContent = `(${count})`;
      if (el.classList.contains('header__cart-count')) {
        el.textContent = count;
        el.hidden = count === 0;
      }
    });
  };

  const renderCartDrawer = async ({ preserveOpen = false } = {}) => {
    const drawer = cartDrawer();
    if (!drawer) return;
    const wasOpen = preserveOpen || drawer.getAttribute('aria-hidden') === 'false';
    try {
      const url = `${window.Shopify.routes.root}?sections=cart-drawer&t=${Date.now()}`;
      const response = await fetch(url, { cache: 'no-store', headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) throw new Error('Cart render failed');
      const data = await response.json();
      const html = data['cart-drawer'];
      if (!html) return;
      const current = document.createRange().createContextualFragment(html).firstElementChild;
      drawer.replaceWith(current);
      if (wasOpen) {
        current.setAttribute('aria-hidden', 'false');
        document.documentElement.classList.add('cart-drawer-open');
      }
    } catch (error) {
      console.warn('[PetIT] Could not refresh cart drawer.', error);
    }
  };

  const getCart = async () => {
    const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Cart request failed');
    return response.json();
  };

  const changeCartLine = async (line, quantity) => {
    const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line, quantity })
    });
    if (!response.ok) throw new Error('Cart update failed');
    return response.json();
  };

  const addToCart = async (form) => {
    const button = qs('[data-add-to-cart], [data-quick-add]', form);
    if (!button || button.disabled) return;

    const original = button.innerHTML;
    button.classList.add('is-loading');
    button.disabled = true;

    const formData = new FormData(form);
    const quantity = Math.max(1, parseInt(formData.get('quantity') || '1', 10));
    formData.set('quantity', quantity);

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || 'Could not add to cart');

      const cart = await getCart();
      updateCartBadges(cart.item_count);
      await renderCartDrawer();
      updateFreeShippingProgress(cart.total_price);
      setCartOpen(true);

      button.innerHTML = '<span>Added ✓</span>';
      setTimeout(() => {
        if (document.body.contains(button)) button.innerHTML = original;
      }, 1200);
    } catch (error) {
      const status = qs('[data-product-status]', form);
      if (status) {
        status.textContent = error.message;
        status.classList.add('is-error');
      } else {
        window.alert(error.message);
      }
    } finally {
      button.classList.remove('is-loading');
      button.disabled = false;
    }
  };

  const initProductForms = () => {
    qsa('[data-product-form], [data-quick-add-form]').forEach((form) => {
      if (form.dataset.bound) return;
      form.dataset.bound = 'true';

      form.addEventListener('submit', (event) => {
        event.preventDefault();
        addToCart(form);
      });

      const variantJson = qs('[data-product-variants]', form);
      if (variantJson) {
        let variants = [];
        try { variants = JSON.parse(variantJson.textContent); } catch (_) {}
        const selects = qsa('[data-option-index]', form);
        const hiddenId = qs('[data-variant-id]', form);
        const price = qs('[data-product-price]');
        const stickyPrice = qs('[data-sticky-price]');
        const addButton = qs('[data-add-to-cart]', form);
        const addPrice = qs('[data-add-price]', form);
        const label = qs('[data-add-label]', form);

        const syncVariant = () => {
          if (!variants.length) return;
          const values = selects.map((select) => select.value);
          const variant = variants.find((v) => v.options.every((option, index) => option === values[index]));
          if (!variant) {
            if (addButton) addButton.disabled = true;
            if (label) label.textContent = 'Unavailable';
            return;
          }

          if (hiddenId) hiddenId.value = variant.id;
          if (addButton) addButton.disabled = !variant.available;
          if (label) label.textContent = variant.available ? 'Add to cart' : 'Sold out';

          if (price) {
            const autoPct = parseFloat(price.dataset.wasPct) || 0;
            let compare = variant.compare_at_price && variant.compare_at_price > variant.price ? variant.compare_at_price : 0;
            const isPawDine = !!price.closest('.product-page--paw-dine-luxury');
            if (isPawDine && !compare) compare = 49998;
            if (!compare && autoPct > 0 && autoPct < 100) compare = Math.floor(variant.price * 100 / (100 - autoPct));
            const sale = compare > variant.price;
            const pct = sale ? Math.round((compare - variant.price) * 100 / compare) : 0;
            const luxuryMoney = (cents) => {
              const value = Number(cents || 0) / 100;
              const amount = value.toFixed(2);
              return `<span class="paw-dine-price"><span class="paw-dine-price__currency">€</span><span class="paw-dine-price__amount">${amount}</span></span>`;
            };
            price.innerHTML = sale
              ? (isPawDine
                ? `${luxuryMoney(variant.price).replace('class="paw-dine-price"', 'class="product-page__price--sale paw-dine-price"')} ${luxuryMoney(compare).replace('class="paw-dine-price"', 'class="product-page__price--compare paw-dine-price paw-dine-price--compare"')} <span class="product-page__price--save">${pct >= 1 ? pct + '% off' : 'Sale'}</span>`
                : `<span class="product-page__price--sale">${formatMoney(variant.price)}</span> <span class="product-page__price--compare">${formatMoney(compare)}</span> <span class="product-page__price--save">${pct >= 1 ? pct + '% off' : 'Sale'}</span>`)
              : (isPawDine ? luxuryMoney(variant.price) : `<span>${formatMoney(variant.price)}</span>`);
          }
          if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
          if (addPrice) addPrice.textContent = variant.available ? formatMoney(variant.price) : '';
          const sticky = qs('[data-sticky-add]');
          if (sticky) sticky.disabled = !variant.available;
        };

        selects.forEach((select) => select.addEventListener('change', syncVariant));
        syncVariant();
      }

      const quantity = qs('[data-quantity]', form);
      qs('[data-qty-minus]', form)?.addEventListener('click', () => {
        if (quantity) quantity.value = Math.max(1, (parseInt(quantity.value, 10) || 1) - 1);
      });
      qs('[data-qty-plus]', form)?.addEventListener('click', () => {
        if (quantity) quantity.value = Math.max(1, (parseInt(quantity.value, 10) || 1) + 1);
      });
    });
  };

  const initGallery = () => {
    const main = qs('[data-product-main-image]');
    if (!main) return;

    const thumbs = qsa('[data-product-media]');
    if (!thumbs.length) return;

    const prev = qs('[data-product-gallery-prev]', main);
    const next = qs('[data-product-gallery-next]', main);
    const currentEl = qs('[data-product-gallery-current]', main);
    let activeIndex = Math.max(0, thumbs.findIndex((thumb) => thumb.classList.contains('product-page__thumb--active')));

    const showImage = (index) => {
      if (!thumbs.length) return;
      activeIndex = (index + thumbs.length) % thumbs.length;
      const button = thumbs[activeIndex];
      const image = qs('img', button);
      const src = button.dataset.mediaUrl;
      if (!src) return;

      main.querySelector('.product-page__img')?.remove();
      const newImage = document.createElement('img');
      newImage.className = 'product-page__img';
      newImage.src = src;
      newImage.alt = image?.alt || '';
      newImage.loading = 'eager';
      main.insertBefore(newImage, main.firstChild);

      thumbs.forEach((thumb, thumbIndex) => {
        const active = thumbIndex === activeIndex;
        thumb.classList.toggle('product-page__thumb--active', active);
        thumb.setAttribute('aria-current', active ? 'true' : 'false');
      });

      if (currentEl) currentEl.textContent = String(activeIndex + 1);
      button.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    };

    thumbs.forEach((button, index) => {
      button.addEventListener('click', () => showImage(index));
    });

    prev?.addEventListener('click', () => showImage(activeIndex - 1));
    next?.addEventListener('click', () => showImage(activeIndex + 1));

    // Enable touch-swipe navigation on phones only; desktop arrows remain unchanged.
    if (window.matchMedia('(max-width: 749px)').matches) {
      let touchStartX = 0;
      let touchStartY = 0;
      let trackingTouch = false;

      main.addEventListener('touchstart', (event) => {
        if (event.touches.length !== 1) {
          trackingTouch = false;
          return;
        }
        touchStartX = event.touches[0].clientX;
        touchStartY = event.touches[0].clientY;
        trackingTouch = true;
      }, { passive: true });

      main.addEventListener('touchend', (event) => {
        if (!trackingTouch || !event.changedTouches.length) return;
        trackingTouch = false;
        const deltaX = event.changedTouches[0].clientX - touchStartX;
        const deltaY = event.changedTouches[0].clientY - touchStartY;
        if (Math.abs(deltaX) < 45 || Math.abs(deltaX) < Math.abs(deltaY) * 1.2) return;
        if (deltaX < 0) showImage(activeIndex + 1);
        else showImage(activeIndex - 1);
      }, { passive: true });
    }

    main.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') showImage(activeIndex - 1);
      if (event.key === 'ArrowRight') showImage(activeIndex + 1);
    });
  };

  const initStickyBuy = () => {
    const sticky = qs('[data-sticky-buy]');
    const mainForm = qs('[data-product-form]');
    if (!sticky || !mainForm) return;
    const observer = new IntersectionObserver(([entry]) => {
      sticky.classList.toggle('is-visible', !entry.isIntersecting);
    }, { threshold: 0.15 });
    observer.observe(mainForm);
    qs('[data-sticky-add]')?.addEventListener('click', () => {
      mainForm.requestSubmit();
    });
  };

  const initFinalProductCta = () => {
    const button = qs('[data-sticky-add-final]');
    const mainForm = qs('[data-product-form]');
    if (!button || !mainForm) return;
    button.addEventListener('click', () => mainForm.requestSubmit());
  };

  const initCart = () => {
    document.addEventListener('click', async (event) => {
      const open = event.target.closest('[data-cart-open]');
      const close = event.target.closest('[data-cart-close]');
      if (open) { event.preventDefault(); event.stopPropagation(); setCartOpen(true); return; }
      if (close) { event.preventDefault(); event.stopPropagation(); setCartOpen(false); return; }

      const drawer = cartDrawer();
      if (!drawer) return;

      const plus = event.target.closest('[data-cart-plus]');
      const minus = event.target.closest('[data-cart-minus]');
      const remove = event.target.closest('[data-cart-remove]');
      const action = plus || minus || remove;
      if (!action) return;

      // Cart controls are real buttons inside the drawer. Never allow the click to
      // fall through to a link/overlay, and never let a browser navigation close the drawer.
      event.preventDefault();
      event.stopPropagation();

      const line = parseInt(action.dataset.line, 10);
      const row = action.closest('[data-cart-line]');
      const qtyEl = qs('.quantity-control--small span', row);
      const currentQty = parseInt(qtyEl?.textContent || '1', 10);
      const nextQty = remove ? 0 : (plus ? currentQty + 1 : Math.max(0, currentQty - 1));
      const previousQty = currentQty;

      // Optimistic UI: the number and bag badge change immediately while Shopify updates in the background.
      if (qtyEl) qtyEl.textContent = nextQty;
      const badge = qs('[data-cart-count]');
      const visibleCount = parseInt(badge?.textContent || '0', 10) || 0;
      const nextCount = Math.max(0, visibleCount + (nextQty - previousQty));
      updateCartBadges(nextCount);

      action.disabled = true;
      row?.classList.add('is-updating');
      try {
        const cart = await changeCartLine(line, nextQty);
        updateCartBadges(cart.item_count);
        updateFreeShippingProgress(cart.total_price);

        // The server is the single source of truth: re-render the drawer so the item list,
        // count, subtotal and free-shipping bar always match the real cart.
        await renderCartDrawer({ preserveOpen: true });
        updateFreeShippingProgress(cart.total_price);
      } catch (error) {
        if (qtyEl) qtyEl.textContent = previousQty;
        updateCartBadges(visibleCount);
        console.warn('[PetIT] Cart update failed.', error);
      } finally {
        action.disabled = false;
        row?.classList.remove('is-updating');
      }
    });

    document.addEventListener('click', async (event) => {
      const toggle = event.target.closest('[data-cart-bundle-toggle]');
      if (toggle) {
        event.preventDefault();
        event.stopPropagation();
        const panel = qs('[data-cart-bundle-panel]');
        if (!panel) return;
        const open = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', String(!open));
        panel.hidden = open;
        return;
      }

      const quick = event.target.closest('[data-cart-bundle-add]');
      if (!quick || quick.disabled) return;
      event.preventDefault();
      event.stopPropagation();
      const variantId = quick.dataset.variantId;
      if (!variantId) return;
      quick.disabled = true;
      const original = quick.textContent;
      quick.textContent = '…';
      try {
        const form = new FormData();
        form.append('id', variantId);
        form.append('quantity', '1');
        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: form });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || 'Could not add to cart');
        const cart = await getCart();
        updateCartBadges(cart.item_count);
        updateFreeShippingProgress(cart.total_price);
        quick.textContent = '✓';
        quick.classList.add('is-added');
        await renderCartDrawer({ preserveOpen: true });
      } catch (error) {
        quick.disabled = false;
        quick.textContent = original;
        console.warn('[PetIT] Quick bundle add failed.', error);
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') setCartOpen(false);
    });
  };

  const initMobileMenu = () => {
    const toggle = qs('[data-mobile-toggle]');
    const mobileNav = qs('[data-mobile-nav]');
    const closeBtn = qs('[data-mobile-close]');
    if (!toggle || !mobileNav) return;
    toggle.addEventListener('click', () => {
      mobileNav.classList.add('is-open');
      document.documentElement.classList.add('mobile-nav-open');
    });
    closeBtn?.addEventListener('click', () => {
      mobileNav.classList.remove('is-open');
      document.documentElement.classList.remove('mobile-nav-open');
    });
  };



  const initPetitHeroLogo = () => {
    const stage = qs('[data-petit-hero-logo]');
    const object = qs('[data-petit-hero-logo-object]');
    if (!stage || !object) return;
    const target = object;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let rotX = -4;
    let rotY = -12;
    let scale = 1;
    let dragging = false;
    let moved = false;
    let lastX = 0;
    let lastY = 0;
    let idleRaf = 0;
    let lastTime = performance.now();

    const render = () => {
      object.style.transform = `translate3d(0,0,0) rotateX(${rotX}deg) rotateY(${rotY}deg) scale(${scale})`;
    };

    const stopIdle = () => {
      if (idleRaf) cancelAnimationFrame(idleRaf);
      idleRaf = 0;
    };

    const idle = (time) => {
      if (dragging || reduce) { idleRaf = 0; return; }
      const dt = Math.min(32, time - lastTime);
      lastTime = time;
      rotY += dt * 0.012;
      rotX += Math.sin(time * 0.00055) * 0.002;
      render();
      idleRaf = requestAnimationFrame(idle);
    };

    const startIdle = () => {
      if (reduce || idleRaf) return;
      lastTime = performance.now();
      idleRaf = requestAnimationFrame(idle);
    };

    const onDown = (event) => {
      dragging = true;
      moved = false;
      lastX = event.clientX;
      lastY = event.clientY;
      stopIdle();
      stage.classList.add('is-dragging');
      target.setPointerCapture?.(event.pointerId);
      event.preventDefault();
    };

    const onMove = (event) => {
      if (!dragging) return;
      const dx = event.clientX - lastX;
      const dy = event.clientY - lastY;
      if (Math.abs(dx) + Math.abs(dy) > 2) moved = true;
      lastX = event.clientX;
      lastY = event.clientY;
      rotY += dx * 0.72;
      rotX -= dy * 0.72;
      render();
    };

    const onUp = (event) => {
      if (!dragging) return;
      dragging = false;
      stage.classList.remove('is-dragging');
      try { target.releasePointerCapture?.(event.pointerId); } catch (_) {}
      if (moved) {
        stage.dataset.dragged = 'true';
        window.setTimeout(() => { delete stage.dataset.dragged; }, 180);
      }
      startIdle();
    };

    target.addEventListener('pointerdown', onDown);
    target.addEventListener('pointermove', onMove);
    target.addEventListener('pointerup', onUp);
    target.addEventListener('pointercancel', onUp);
    target.addEventListener('click', (event) => {
      if (stage.dataset.dragged === 'true') {
        event.preventDefault();
        event.stopPropagation();
      }
    });

    target.addEventListener('wheel', (event) => {
      event.preventDefault();
      rotY += event.deltaX * 0.35 + event.deltaY * 0.12;
      rotX -= event.deltaY * 0.08;
      render();
    }, { passive: false });

    render();
    startIdle();
  };

  // Interactive 3D logo (contact page): drag to spin it, hover/tap for a full 360 turn, slow idle drift.
  const initSpinLogos = () => {
    qsa('[data-spin-logo]').forEach((el) => {
      if (el.dataset.bound) return;
      el.dataset.bound = 'true';

      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const IDLE = 0.012; // degrees per ms, slow drift like the homepage logo
      let rotX = 0;
      let rotY = 0;
      let vel = 0;        // inertia after a drag (deg per ms)
      let spinLeft = 0;   // degrees still to travel for a hover/tap 360 turn
      let dragging = false;
      let moved = false;
      let lastX = 0;
      let lastY = 0;
      let lastT = 0;
      let raf = 0;
      let last = 0;

      const render = () => {
        el.style.transform = `perspective(900px) rotateX(${rotX}deg) rotateY(${rotY}deg)`;
      };

      const spin = () => { if (spinLeft < 90) spinLeft += 360; };

      const tick = (time) => {
        const dt = Math.min(48, (time - last) || 16);
        last = time;
        if (!dragging) {
          if (Math.abs(vel) > 0.0004) { rotY += vel * dt; vel *= Math.pow(0.92, dt / 16); } else { vel = 0; }
          if (spinLeft > 0.2) {
            const step = spinLeft * (1 - Math.exp(-dt / 230));
            rotY += step;
            spinLeft -= step;
          } else if (spinLeft) {
            rotY += spinLeft;
            spinLeft = 0;
          }
          if (!reduce) rotY += IDLE * dt;
          rotX *= Math.pow(0.9, dt / 16);
        }
        render();
        raf = requestAnimationFrame(tick);
      };
      const start = () => { if (raf) return; last = performance.now(); raf = requestAnimationFrame(tick); };
      const stop = () => { if (raf) cancelAnimationFrame(raf); raf = 0; };

      el.addEventListener('pointerdown', (event) => {
        dragging = true;
        moved = false;
        vel = 0;
        spinLeft = 0;
        lastX = event.clientX;
        lastY = event.clientY;
        lastT = event.timeStamp;
        el.classList.add('is-dragging');
        try { el.setPointerCapture(event.pointerId); } catch (_) {}
        if (event.pointerType === 'mouse') event.preventDefault();
      });

      el.addEventListener('pointermove', (event) => {
        if (!dragging) return;
        const dx = event.clientX - lastX;
        const dy = event.clientY - lastY;
        if (Math.abs(dx) + Math.abs(dy) > 2) moved = true;
        const dtm = Math.max(1, event.timeStamp - lastT);
        rotY += dx * 0.72;
        rotX = Math.max(-50, Math.min(50, rotX - dy * 0.5));
        vel = (dx * 0.72) / dtm;
        lastX = event.clientX;
        lastY = event.clientY;
        lastT = event.timeStamp;
      });

      const release = (event) => {
        try { el.releasePointerCapture(event.pointerId); } catch (_) {}
        el.classList.remove('is-dragging');
      };
      el.addEventListener('pointerup', (event) => {
        if (!dragging) return;
        dragging = false;
        release(event);
        if (event.timeStamp - lastT > 80) vel = 0;
        vel = Math.max(-2, Math.min(2, vel));
        if (!moved) spin();
      });
      el.addEventListener('pointercancel', (event) => {
        dragging = false;
        release(event);
      });
      el.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse' && !dragging) spin();
      });
      el.addEventListener('dragstart', (event) => event.preventDefault());

      if ('IntersectionObserver' in window) {
        new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting) start(); else stop();
        }).observe(el);
      } else {
        start();
      }
      render();
    });
  };

  const initPremiumMotion = () => {
    if (!document.body.classList.contains('motion-enabled')) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // Reveal only content that is below the initial viewport. The hero/LCP remains visible immediately.
    const revealTargets = qsa('.section:not(.hero), .page-section, .newsletter, .editorial, .product-grid, .category-grid, .trust-grid, .section-heading, .product-page__description, .product-page__details');
    revealTargets.forEach((el) => {
      if (el.closest('.hero')) return;
      el.classList.add('motion-reveal');
    });

    qsa('.category-grid, .product-grid, .trust-grid').forEach((grid) => {
      grid.classList.remove('motion-reveal');
      grid.classList.add('motion-stagger');
    });

    const io = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

    qsa('.motion-reveal, .motion-stagger').forEach((el) => io.observe(el));

    // Small pointer movement on desktop adds depth without moving layout.
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      qsa('.hero__inner').forEach((hero) => {
        let raf = 0;
        hero.addEventListener('pointermove', (event) => {
          if (raf) return;
          raf = requestAnimationFrame(() => {
            const rect = hero.getBoundingClientRect();
            const x = ((event.clientX - rect.left) / rect.width - .5) * 2;
            const y = ((event.clientY - rect.top) / rect.height - .5) * 2;
            hero.style.setProperty('--hero-x', `${x.toFixed(3)}`);
            hero.style.setProperty('--hero-y', `${y.toFixed(3)}`);
            raf = 0;
          });
        }, { passive: true });
        hero.addEventListener('pointerleave', () => {
          hero.style.setProperty('--hero-x', '0');
          hero.style.setProperty('--hero-y', '0');
        }, { passive: true });
      });
    }

    // Selective View Transitions for normal internal navigation. Never intercept forms or checkout.
    if ('startViewTransition' in document) {
      document.addEventListener('click', (event) => {
        const link = event.target.closest('a[href]');
        if (!link || event.defaultPrevented || link.target === '_blank') return;
        const url = new URL(link.href, location.href);
        if (url.origin !== location.origin || url.pathname === location.pathname + url.search || url.pathname === '/checkout') return;
        if (link.closest('form, [data-cart-drawer], .mobile-nav')) return;
        if (url.pathname.startsWith('/account')) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        // Let the browser's cross-document view-transition opt-in handle the actual navigation.
      }, { passive: true });
    }
  };

  document.addEventListener('DOMContentLoaded', () => {
    [
      initSpinLogos,
      initProductForms,
      initGallery,
      initStickyBuy,
      initFinalProductCta,
      initCart,
      initMobileMenu,
      initPetitHeroLogo,
      initPremiumMotion
    ].forEach((init) => {
      try { init(); } catch (error) { console.warn('[PetIT] init failed:', init.name, error); }
    });
  });
})();
